import { useState, type ReactNode } from 'react';

import { ModalComponent } from '@/components/modal';
import { MODAL_TABLE_SIZE_STYLES } from '@/components/modal/constants';
import { TableSizeForm } from '@/components/modals/table-size-modal/table-size-modal';
import {
  insertRow,
  removeRow,
  insertColumn,
  removeColumn,
  mergeCells,
  splitCell,
  setHeaderRowCount,
  setCaption,
  resizeTable,
  tableResizeWouldLoseData,
  type CellSelection,
} from '@/lib/table-ops';
import { type DocumentEntity } from '@/context/store/slices/documentDataSlice';

type TableNode = DocumentEntity & { type: 'table' };

type Props = {
  node: TableNode;
  /** Latest table including unflushed cell draft rows. */
  getWorkingNode: () => TableNode;
  /** Commit a structural/caption change (draft is already folded in). */
  apply: (next: TableNode) => void;
  focusedCell: { r: number; c: number } | null;
  selection: CellSelection | null;
  /** Precomputed by the parent so the toolbar never re-expands the grid. */
  tableRows: number;
  tableCols: number;
  isSplittable: boolean;
};

interface IconButtonProps {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  children: ReactNode;
}

function IconButton({ onClick, disabled, label, children }: IconButtonProps) {
  return (
    <button
      type="button"
      className="tt-iconbtn"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

// --- Inline SVG icons. Bespoke, sized for a 28px button. ---------------
// Each icon depicts a tiny table grid with an action marker drawn on it,
// so the user reads "row above / row below / col left / col right" without
// having to translate generic chevrons.

const stroke = 'currentColor';

function RowInsertAbove() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <path d="M2 11h12M2 14h12" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M8 1v6M5 4h6" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function RowInsertBelow() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <path d="M2 2h12M2 5h12" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M8 9v6M5 12h6" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function RowDelete() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <path d="M2 3h12M2 13h12" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" opacity="0.6" />
      <path d="M2 8h12" stroke="var(--tt-danger)" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M11 5l3 3-3 3" stroke="var(--tt-danger)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ColInsertLeft() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <path d="M11 2v12M14 2v12" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M1 8h6M4 5v6" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ColInsertRight() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <path d="M2 2v12M5 2v12" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M9 8h6M12 5v6" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ColDelete() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <path d="M3 2v12M13 2v12" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" opacity="0.6" />
      <path d="M8 2v12" stroke="var(--tt-danger)" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M5 11l3 3 3-3" stroke="var(--tt-danger)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MergeIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <rect x="1.5" y="3.5" width="6" height="9" rx="1" stroke={stroke} strokeWidth="1.3" />
      <rect x="8.5" y="3.5" width="6" height="9" rx="1" stroke={stroke} strokeWidth="1.3" />
      <path d="M6 8h4" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M9 6l2 2-2 2" stroke={stroke} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SplitIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <rect x="2" y="3.5" width="12" height="9" rx="1" stroke={stroke} strokeWidth="1.3" />
      <path d="M8 4.5v7" stroke={stroke} strokeWidth="1.4" strokeDasharray="1.5 1.5" strokeLinecap="round" />
      <path d="M5 8h6" stroke={stroke} strokeWidth="1.3" strokeLinecap="round" />
      <path d="M7 6l-2 2 2 2M9 6l2 2-2 2" stroke={stroke} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

function GridSizeIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <rect x="2" y="2" width="12" height="12" rx="1" stroke={stroke} strokeWidth="1.3" />
      <path d="M2 6h12M2 10h12M6 2v12M10 2v12" stroke={stroke} strokeWidth="1" opacity="0.7" />
    </svg>
  );
}

export function TableToolbar({
  node,
  getWorkingNode,
  apply,
  focusedCell,
  selection,
  tableRows,
  tableCols,
  isSplittable,
}: Props) {
  const [sizeModalOpen, setSizeModalOpen] = useState(false);

  const cellDisabled = !focusedCell;
  const totalColumns = tableCols;

  const isMergeable =
    selection !== null &&
    (selection.r1 !== selection.r2 || selection.c1 !== selection.c2);

  const headerRowCount = node.headerRowCount ?? 0;

  function withDraft(op: (working: TableNode) => TableNode): void {
    apply(op(getWorkingNode()));
  }

  function handleResizeConfirm(newRows: number, newCols: number): void {
    setSizeModalOpen(false);
    const working = getWorkingNode();
    if (tableResizeWouldLoseData(working, newRows, newCols)) {
      const ok = window.confirm(
        'Resizing the table will remove content in cells outside the new dimensions. Continue?',
      );
      if (!ok) return;
    }
    apply(resizeTable(working, newRows, newCols));
  }

  return (
    <>
    <div className="doc-editor-table__local-toolbar tt">
      <div className="tt-row">
        <div className="tt-group" data-label="Rows">
          <IconButton
            disabled={cellDisabled}
            label="Insert row above"
            onClick={() =>
              focusedCell &&
              withDraft(n => insertRow(n, focusedCell.r, 'above'))
            }
          >
            <RowInsertAbove />
          </IconButton>
          <IconButton
            disabled={cellDisabled}
            label="Insert row below"
            onClick={() =>
              focusedCell &&
              withDraft(n => insertRow(n, focusedCell.r, 'below'))
            }
          >
            <RowInsertBelow />
          </IconButton>
          <IconButton
            disabled={cellDisabled || node.rows.length <= 1}
            label="Delete row"
            onClick={() =>
              focusedCell && withDraft(n => removeRow(n, focusedCell.r))
            }
          >
            <RowDelete />
          </IconButton>
        </div>

        <div className="tt-divider" />

        <div className="tt-group" data-label="Columns">
          <IconButton
            disabled={cellDisabled}
            label="Insert column left"
            onClick={() =>
              focusedCell &&
              withDraft(n => insertColumn(n, focusedCell.c, 'left'))
            }
          >
            <ColInsertLeft />
          </IconButton>
          <IconButton
            disabled={cellDisabled}
            label="Insert column right"
            onClick={() =>
              focusedCell &&
              withDraft(n => insertColumn(n, focusedCell.c, 'right'))
            }
          >
            <ColInsertRight />
          </IconButton>
          <IconButton
            disabled={cellDisabled || totalColumns <= 1}
            label="Delete column"
            onClick={() =>
              focusedCell && withDraft(n => removeColumn(n, focusedCell.c))
            }
          >
            <ColDelete />
          </IconButton>
        </div>

        <div className="tt-divider" />

        <div className="tt-group" data-label="Cells">
          <IconButton
            disabled={!isMergeable}
            label="Merge cells"
            onClick={() =>
              selection && withDraft(n => mergeCells(n, selection))
            }
          >
            <MergeIcon />
          </IconButton>
          <IconButton
            disabled={!isSplittable}
            label="Split cell"
            onClick={() =>
              focusedCell &&
              withDraft(n => splitCell(n, focusedCell.r, focusedCell.c))
            }
          >
            <SplitIcon />
          </IconButton>
        </div>

        <div className="tt-flex" />

        <div className="tt-stepper">
          <span className="tt-stepper__label">Header rows</span>
          <button
            type="button"
            className="tt-stepper__btn"
            disabled={headerRowCount <= 0}
            onClick={() =>
              withDraft(n =>
                setHeaderRowCount(n, Math.max(0, headerRowCount - 1)),
              )
            }
            aria-label="Decrement header rows"
          >
            −
          </button>
          <span className="tt-stepper__value">{headerRowCount}</span>
          <button
            type="button"
            className="tt-stepper__btn"
            disabled={headerRowCount >= node.rows.length}
            onClick={() =>
              withDraft(n =>
                setHeaderRowCount(
                  n,
                  Math.min(n.rows.length, headerRowCount + 1),
                ),
              )
            }
            aria-label="Increment header rows"
          >
            +
          </button>
        </div>

        <div className="tt-group" data-label="Size">
          <IconButton
            label="Table size"
            onClick={() => setSizeModalOpen(true)}
          >
            <GridSizeIcon />
          </IconButton>
        </div>
      </div>

      <label className="tt-caption">
        <span className="tt-caption__label">Caption</span>
        <input
          className="tt-caption__input"
          type="text"
          placeholder="Add a caption to describe this table"
          value={node.caption ?? ''}
          onChange={e => withDraft(n => setCaption(n, e.target.value))}
        />
      </label>
    </div>
    <ModalComponent
      isOpen={sizeModalOpen}
      setIsOpen={setSizeModalOpen}
      title="Table size"
      style={MODAL_TABLE_SIZE_STYLES}
      body={
        <TableSizeForm
          initialRows={tableRows || 3}
          initialCols={tableCols || 3}
          title="Set table dimensions"
          confirmLabel="Apply size"
          onConfirm={handleResizeConfirm}
          onCancel={() => setSizeModalOpen(false)}
        />
      }
    />
    </>
  );
}
